import React, { useEffect, useState } from 'react';
import { Instagram, ArrowRight } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import { ADMIN_LOGIN_PATH } from '../App';

const SESSION_KEY = 'outlaw_intro_seen';
export const INSTAGRAM_URL = 'https://instagram.com/ryannaumenko';
export const X_URL = 'https://x.com/ryan_naumenko';

/** Entry splash: a breaking-news flash playing on a CRT television, with a
 *  follow button for the new Instagram and an enter-site option. Shown once
 *  per visit, dead-centred and sized off dynamic viewport units so it lands
 *  perfectly in in-app browsers (x.com, Instagram) as well as desktop. */
const BreakingNewsIntro = () => {
  const { pathname } = useLocation();
  const [show, setShow] = useState(false);

  useEffect(() => {
    if (pathname.startsWith(ADMIN_LOGIN_PATH)) return;
    let seen = null;
    try { seen = sessionStorage.getItem(SESSION_KEY); } catch { /* ignore */ }
    if (!seen) setShow(true);
  }, [pathname]);

  useEffect(() => {
    if (!show) return undefined;
    document.body.style.overflow = 'hidden';
    const onKey = (e) => { if (e.key === 'Escape') dismiss(); };
    window.addEventListener('keydown', onKey);
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [show]);

  const dismiss = () => {
    try { sessionStorage.setItem(SESSION_KEY, '1'); } catch { /* ignore */ }
    setShow(false);
    document.body.style.overflow = '';
  };

  if (!show) return null;

  return (
    <div
      data-testid="intro-video-overlay"
      role="dialog"
      aria-modal="true"
      aria-label="Breaking news - follow the new Instagram"
      className="fixed inset-0 z-[90] bg-black flex items-center justify-center p-4"
      style={{ minHeight: '100dvh', paddingBottom: 'max(1rem, env(safe-area-inset-bottom))' }}
    >
      <style>{`
        @keyframes om-tv-flicker {
          0%, 100% { opacity: 1; }
          92% { opacity: 1; }
          94% { opacity: 0.82; }
          96% { opacity: 1; }
          98% { opacity: 0.9; }
        }
        @keyframes om-live-blink { 0%, 60% { opacity: 1; } 80%, 100% { opacity: 0.15; } }
        @keyframes om-ticker { from { transform: translateX(0); } to { transform: translateX(-50%); } }
        @keyframes om-sweep { from { transform: translateY(-120%); } to { transform: translateY(420%); } }
        .om-tv-screen { animation: om-tv-flicker 6s infinite steps(1, end); }
        .om-scanlines {
          background-image: repeating-linear-gradient(
            to bottom,
            rgba(0,0,0,0.38) 0px,
            rgba(0,0,0,0.38) 1px,
            rgba(0,0,0,0) 2px,
            rgba(0,0,0,0) 4px
          );
        }
        .om-ticker-track { animation: om-ticker 18s linear infinite; }
        .om-sweep { animation: om-sweep 7s linear infinite; }
        @media (prefers-reduced-motion: reduce) {
          .om-tv-screen, .om-ticker-track, .om-sweep { animation: none !important; }
        }
      `}</style>

      {/* TV set */}
      <div className="w-full max-w-[560px] mx-auto">
        <div
          data-testid="intro-tv"
          className="relative rounded-[26px] border border-zinc-700/80 bg-gradient-to-b from-zinc-800 via-zinc-900 to-black p-3 sm:p-4 shadow-[0_0_80px_rgba(220,38,38,0.25),inset_0_1px_0_rgba(255,255,255,0.08)]"
        >
          {/* Screen */}
          <div className="om-tv-screen relative overflow-hidden rounded-[16px] bg-[#0a0a0a] aspect-[4/3] border border-black">
            {/* Glow + vignette */}
            <div aria-hidden="true" className="absolute inset-0 bg-[radial-gradient(ellipse_at_center,rgba(220,38,38,0.18),transparent_65%)]" />
            <div aria-hidden="true" className="absolute inset-0 shadow-[inset_0_0_90px_rgba(0,0,0,0.95)]" />

            {/* Broadcast content */}
            <div className="relative z-10 h-full flex flex-col">
              <div className="flex items-center justify-between gap-2 px-3 pt-3 sm:px-4 sm:pt-4">
                <span className="inline-flex items-center gap-1.5 rounded-sm bg-red-600 px-2 py-1 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-white">
                  <span className="h-1.5 w-1.5 rounded-full bg-white" style={{ animation: 'om-live-blink 1.2s infinite' }} />
                  Live
                </span>
                <span className="text-[9px] sm:text-[10px] font-black uppercase tracking-[0.25em] text-white/60">
                  Outlaw Media
                </span>
              </div>

              <div className="flex-1 flex flex-col items-center justify-center text-center px-4 sm:px-7">
                <p className="text-red-500 text-[10px] sm:text-xs font-black uppercase tracking-[0.35em] mb-2 sm:mb-3">
                  Breaking News
                </p>
                <h1
                  className="text-white font-black uppercase leading-[1.05] text-[22px] sm:text-[32px] mb-2 sm:mb-3"
                  style={{ fontFamily: 'Georgia, serif', textShadow: '0 2px 18px rgba(220,38,38,0.5)' }}
                  data-testid="intro-headline"
                >
                  Our Instagram
                  <br />
                  Has Been Banned
                </h1>
                <p className="text-zinc-300 text-[11px] sm:text-sm leading-relaxed max-w-[38ch]">
                  Taken down off the back of false reports and Meta's woeful AI enforcement - no
                  warning, no human, no appeal. Follow the new account so they can't cut us off
                  from you again. Not on Instagram? We're on X too.
                </p>
              </div>

              {/* Lower-third ticker */}
              <div className="relative border-t-2 border-red-600 bg-black/85 backdrop-blur-sm">
                <div className="flex items-stretch">
                  <span className="shrink-0 bg-red-600 px-2.5 sm:px-3 py-2 text-[9px] sm:text-[10px] font-black uppercase tracking-[0.2em] text-white flex items-center">
                    New Account
                  </span>
                  <div className="relative flex-1 overflow-hidden py-2">
                    <div className="om-ticker-track flex whitespace-nowrap text-[10px] sm:text-xs font-bold uppercase tracking-[0.2em] text-white/85">
                      {[0, 1].map((i) => (
                        <span key={i} className="flex shrink-0">
                          <span className="px-4">Follow @ryannaumenko on Instagram</span>
                          <span className="px-4 text-red-500">+++</span>
                          <span className="px-4">Or follow @ryan_naumenko on X</span>
                          <span className="px-4 text-red-500">+++</span>
                          <span className="px-4">Old page banned on false reports</span>
                          <span className="px-4 text-red-500">+++</span>
                          <span className="px-4">Melbourne crime reporting continues</span>
                          <span className="px-4 text-red-500">+++</span>
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* CRT texture */}
            <div aria-hidden="true" className="om-scanlines pointer-events-none absolute inset-0 z-20 opacity-60 mix-blend-multiply" />
            <div aria-hidden="true" className="om-sweep pointer-events-none absolute inset-x-0 z-20 h-24 bg-gradient-to-b from-transparent via-white/5 to-transparent" />
            <div aria-hidden="true" className="pointer-events-none absolute inset-0 z-20 rounded-[16px] bg-[linear-gradient(115deg,rgba(255,255,255,0.08)_0%,transparent_38%)]" />
          </div>

          {/* Bezel detail */}
          <div className="mt-3 flex items-center justify-between px-1">
            <span className="text-[9px] font-black uppercase tracking-[0.3em] text-zinc-500">Outlaw TV</span>
            <span className="flex items-center gap-1.5">
              <span className="h-1.5 w-1.5 rounded-full bg-red-600 shadow-[0_0_8px_rgba(220,38,38,0.9)]" />
              <span className="h-1.5 w-6 rounded-full bg-zinc-700" />
            </span>
          </div>
        </div>

        {/* Actions */}
        <div className="mt-5 flex flex-col sm:flex-row gap-3 justify-center">
          <a
            href={INSTAGRAM_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="intro-instagram-follow"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-red-600 hover:bg-red-500 text-white font-black text-[11px] sm:text-xs uppercase tracking-[0.25em] transition-transform hover:-translate-y-0.5"
          >
            <Instagram className="h-4 w-4" />
            Follow @ryannaumenko
          </a>
          <a
            href={X_URL}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="intro-x-follow"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white text-black hover:bg-zinc-200 font-black text-[11px] sm:text-xs uppercase tracking-[0.25em] transition-transform hover:-translate-y-0.5"
          >
            <span className="text-base leading-none font-black">𝕏</span>
            Follow On X
          </a>
        </div>

        <div className="mt-3 flex justify-center">
          <button
            type="button"
            onClick={dismiss}
            data-testid="intro-enter-site"
            className="inline-flex items-center justify-center gap-2 px-6 py-3.5 rounded-full bg-white/5 hover:bg-white/15 border border-white/25 text-white font-black text-[11px] sm:text-xs uppercase tracking-[0.25em] transition-colors"
          >
            Enter Site
            <ArrowRight className="h-4 w-4" />
          </button>
        </div>
      </div>
    </div>
  );
};

export default BreakingNewsIntro;
